/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { P } from "@cd2/text/build/es6"
import { IComponentProps, truncate } from "@cd2/tools/build/es6"
import React from "react"
import { QuickToolTip } from "./QuickTooltip"

export function TextWithNewLines({
  text,
  paragraphProps = {},
  stripHtml,
}: {
  text: string
  paragraphProps?: Omit<IComponentProps<typeof P>, "children">
  stripHtml?: boolean
}) {
  const paragraphs = toParagraphs(stripHtml ? basicStripHtml(text) : text)

  return (
    <React.Fragment>
      {paragraphs.map((p, i) => (
        <P
          key={i}
          {...paragraphProps}
          css={css`
            // FIXME: the line height prop seems to be ignored
            line-height: ${paragraphProps.lineHeight || 1.5} !important;
            ${i === paragraphs.length - 1 &&
            css`
              margin-bottom: 0 !important;
            `}
          `}
        >
          {p.split("\n").map((line, j) => (
            <React.Fragment key={j}>
              {j > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </P>
      ))}
    </React.Fragment>
  )
}

export function TextWithNewLinesPopout({ text, stripHtml }: { text: string; stripHtml?: boolean }) {
  text = stripHtml ? basicStripHtml(text) : text
  const paragraphs = toParagraphs(text)

  if (paragraphs.length === 0) {
    return null
  }

  const preview = truncate(paragraphs[0].split("\n")[0])

  return (
    <QuickToolTip
      content={<TextWithNewLines text={text} stripHtml={stripHtml} />}
      trigger="click"
      isLight
      interactive
    >
      <span
        css={css`
          text-decoration: underline dotted;
        `}
      >
        {preview}
      </span>
    </QuickToolTip>
  )
}

function toParagraphs(src: string) {
  return src
    .trim()
    .split("\n\n")
    .map(p => p.trim())
    .filter(Boolean)
}

export function basicStripHtml(src: string) {
  // convert headings to normal paragraphs
  src = src.replace(/<h\d>/g, "<p>")
  src = src.replace(/<\/h\d>/g, "</p>")

  // remove <p> tags and make sure each paragraph is separated by two new lines
  src = src.replace(/<\/p>/g, "\n\n")
  src = src.replace(/<p>/g, "")

  // replace &nbsp;
  src = src.replace(/&nbsp;/g, " ")

  // replace <a> tags with their href
  src = src.replace(/<a href="([^"]*)">[^<]*<\/a>/g, "$2")

  // replace <li> with new lines and a hyphen
  src = src.replace(/<li>/g, "\n- ")
  src = src.replace(/<\/li>/g, "")

  // remove ul and ol tags
  src = src.replace(/<\/?ul>/g, "")
  src = src.replace(/<\/?ol>/g, "")

  // remove images
  src = src.replace(/<img[^>]*>/g, "")

  // replace <br> tags with new lines
  src = src.replace(/<br>/g, "\n")

  // remove all other tags
  src = src.replace(/<[^>]*>/g, "")

  // now split by \n\n filter empty paragraphs and replace muliple spaces in each paragraph with a single space
  src = src
    .split("\n\n")
    .map(p =>
      p
        .split("\n")
        .map(line => line.replace(/\s+/g, " ").trim())
        .join("\n"),
    )
    .filter(Boolean)
    .join("\n\n")

  return src.trim()
}
